import { MouseEventHandler, useEffect, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import RealmSelector from 'SandboxModule/components/RealmSelector';
import { ServerNameSelector } from './ServerNameSelector';
import { useCreateDumpFile } from '../hooks/useCreateDumpFile';
import { StyledDefaultPrimaryButton, StyledForm } from 'styles';
import { MinimalCard } from 'components/styled';
import { conditionallyRenderElement } from 'utils';
import { Servers } from '@constants/server';
import { THREE_MINUTES } from '@constants';
import { useInterval, useLocalStorage } from 'react-use';
import { LocalStorageKeys } from 'core/apiClient';

export const DumpFileRequestCard = () => {
  const { selectedTenant, onSelectRealm, selectedRealm, onSelectTenantRealm } =
    useTenantRealmDropdownsFlow();

  const [dumpFileRequestParams, setDumpFileRequestParams] = useState<{
    serviceName: Nullable<Servers>;
    teamIdsInput: string;
  }>({
    serviceName: null,
    teamIdsInput: '',
  });
  const { serviceName, teamIdsInput } = dumpFileRequestParams;
  const { createDumpFile, isCreatingDumpFile, dumpFile } = useCreateDumpFile();

  // The team field accepts one or more comma-separated team IDs (all in the same realm).
  const teamIdTokens = teamIdsInput
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
  // Digits-only so values like "1e2" or "0x10" are rejected rather than silently coerced
  // by Number(); also rejects non-positive IDs.
  const areTeamIdsValid =
    teamIdTokens.length > 0 &&
    teamIdTokens.every((token) => /^\d+$/.test(token) && Number(token) > 0);
  const parsedTeamIds = teamIdTokens.map(Number);
  const [
    lastTriggerDumpFileRequestTimeStamp,
    setTriggerDumpFileRequestTimeStamp,
    removeDumpFileRequestTimeStamp,
  ] = useLocalStorage<number | undefined>(
    LocalStorageKeys.LastTriggerDumpFileRequestTime,
    undefined,
  );

  /**
   * Time has passed since the last dump file request was triggered.
   */
  const calculateCooldownState = () => {
    if (!lastTriggerDumpFileRequestTimeStamp) {
      return {
        hasPassedCooldownTime: true,
        remainingMsUntilNextRequestEnabled: null,
        cooldownEndTime: null,
      };
    }

    const elapsed = Date.now() - lastTriggerDumpFileRequestTimeStamp;

    const remainingMsUntilNextRequestEnabled = THREE_MINUTES - elapsed;

    const hasPassedCooldownTime = elapsed >= THREE_MINUTES;

    const cooldownEndTime = lastTriggerDumpFileRequestTimeStamp + THREE_MINUTES;

    return {
      hasPassedCooldownTime,
      remainingMsUntilNextRequestEnabled,
      cooldownEndTime,
    };
  };

  const [cooldownState, setCooldownState] = useState(() => calculateCooldownState());

  useEffect(() => {
    setCooldownState(calculateCooldownState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastTriggerDumpFileRequestTimeStamp]);

  const { hasPassedCooldownTime, remainingMsUntilNextRequestEnabled, cooldownEndTime } =
    cooldownState;

  const [coolDownRemainingTime, setCoolDownRemainingTime] = useState(0);

  useInterval(
    () => {
      if (cooldownEndTime) {
        const seconds = Math.floor((cooldownEndTime - Date.now()) / 1000);

        setCoolDownRemainingTime(seconds);
      }
    },
    cooldownEndTime ? 1000 : null,
  );

  // Remove timestamp when cooldown is over
  useInterval(removeDumpFileRequestTimeStamp, remainingMsUntilNextRequestEnabled);

  const onSubmit: React.MouseEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    if (selectedRealm && selectedTenant && areTeamIdsValid && serviceName) {
      createDumpFile({
        realmId: selectedRealm.realm_id,
        tenantName: selectedTenant.tenantName,
        serviceName,
        teamIds: parsedTeamIds,
      });

      // Start cooldown
      const triggerDumpFileRequestTimestamp = Date.now();

      setTriggerDumpFileRequestTimeStamp(triggerDumpFileRequestTimestamp);
    }
  };

  const onDumpFileFieldsChange: React.ChangeEventHandler<any> = (event) => {
    const { value, name } = event.target;
    setDumpFileRequestParams((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const realmDropdownRef = useRef<HTMLDivElement>(null);

  const onRealmSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (selectedTenant) {
      onSelectRealm({
        target: e.currentTarget,
        tenantId: selectedTenant.id,
      });
    }
  };

  const onTenantSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (realmDropdownRef.current) {
      onSelectTenantRealm({
        tenantDropdownTarget: e.currentTarget,
        realmDropdownTarget: realmDropdownRef.current,
        onDone: ({ tenant, realm }) => {
          if (tenant && realm) {
            setDumpFileRequestParams((prevParams) => ({
              ...prevParams,
              tenantName: tenant.id,
              realmId: realm.realm_id,
            }));
          }
        },
      });
    }
  };

  return (
    <MinimalCard sx={{ height: '100%' }}>
      <Box mb={2}>
        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          Team Database Dump Request
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create a dump of a single team database in S3
        </Typography>
      </Box>
      <StyledForm onSubmit={onSubmit}>
        <Box display={'flex'} flexDirection={'column'} alignItems={'stretch'} gap={20}>
          <Box>
            <Box component="h4" sx={{ m: 0, mb: 2, fontWeight: 600 }}>
              Organization
            </Box>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              {selectedTenant ? (
                <TenantSelector onClick={onTenantSelectorClick} tenantId={selectedTenant.id} />
              ) : (
                <></>
              )}
              <RealmSelector
                onClick={onRealmSelectorClick}
                realmId={selectedRealm?.realm_id || ''}
                ref={realmDropdownRef}
              />
            </Box>
          </Box>
          <TextField
            label="Mosaic Team Id(s)"
            name="teamIdsInput"
            value={teamIdsInput}
            onChange={onDumpFileFieldsChange}
            placeholder="e.g. 123 or 123,456,789"
            helperText="One or more team IDs in the same realm, comma-separated"
            error={teamIdsInput.trim().length > 0 && !areTeamIdsValid}
            required
          />
          <ServerNameSelector
            value={dumpFileRequestParams.serviceName || ''}
            onChange={onDumpFileFieldsChange as any}
            disabled={isCreatingDumpFile}
            required
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'stretch'}
          gap={6}
          sx={{ mt: 'auto' }}
        >
          {conditionallyRenderElement({
            isLoading: isCreatingDumpFile,
            error: null,
            content: dumpFile && (
              <Box display={'flex'}>
                <p>
                  Requested a new dump for
                  <br />
                  Tenant: {dumpFile.tenantName}
                  <br />
                  Realm: {dumpFile.realmId}
                  <br />
                  {dumpFile.teamIds?.length ? (
                    <>
                      Team(s): {dumpFile.teamIds.join(', ')}
                      <br />
                    </>
                  ) : null}
                  <br />
                  The dump will be available as <strong>{dumpFile.fileName}</strong> at{' '}
                  <a href={dumpFile.url}>{dumpFile.url}</a> shortly.
                  <br />
                  <br />
                  Creating a dump file may take some time, depending on the size of the database
                  being dumped.
                </p>
              </Box>
            ),
          })}
          <StyledDefaultPrimaryButton
            variant="contained"
            type="submit"
            disabled={isCreatingDumpFile || !hasPassedCooldownTime || !areTeamIdsValid}
          >
            {coolDownRemainingTime > 0 ? `${coolDownRemainingTime}s Remaining` : 'Request'}
          </StyledDefaultPrimaryButton>
          {!hasPassedCooldownTime ? (
            <Box alignSelf={'center'} fontWeight={500} color={'red'}>
              Please wait for 3 minutes before sending the next request.
            </Box>
          ) : (
            <></>
          )}
        </Box>
      </StyledForm>
    </MinimalCard>
  );
};
